import { ShareAltOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Col, Result, Row, Tabs, TabsProps, message } from 'antd';
import { Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import '../../styles/fix/input-dark.scss';
import { AccountService } from 'src/services/accountService';
import { AssetCountResponseDto, AssetPublishModel } from 'src/models/assetModel';
import { HttpStatusCode } from 'axios';
import { ArtistProfileModel, FollowCreateModel } from 'src/models/accountModel';
import { useParams } from 'react-router-dom';
import ShareModal from 'src/components/modal/shareModal';
import { HttpStatus } from 'src/constant/responseStatus';
import { PageDocument } from 'src/constant/stringConstants';
import TranslationKey from 'src/i18n/translation';
import { useSessionContext } from '../../context/session-context';
import { ArtistService } from 'src/services/artistService';
import GalleryArtistCollection from 'src/components/gallery/GalleryArtistCollection';
import { CollectionAssetModel } from 'src/models/CollectionModel';
import { ArtistAssetType } from 'src/constant/artistAssetTypeConstant';
import GalleryArtist from 'src/components/gallery/GalleryArtist';

const { Title, Text } = Typography;

export default function ArtistProfile() {
  const [assets, setAssets] = useState<AssetPublishModel[]>([]);
  const [collections, setCollections] = useState<CollectionAssetModel[]>([]);
  const [artist, setArtist] = useState<ArtistProfileModel>({} as ArtistProfileModel);
  const { id } = useParams();
  const [isNotFound, setIsNotFound] = useState(false);
  const [isOpenShare, setIsOpenShare] = useState(false);
  const [isFollow, setIsFollow] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { userProfile } = useSessionContext();

  const [assetCount, setAssetCount] = useState<AssetCountResponseDto>({
    all: { assetType: ArtistAssetType.All, count: 0 },
    photo: { assetType: ArtistAssetType.Image, count: 0 },
    video: { assetType: ArtistAssetType.Video, count: 0 },
    collection: { assetType: ArtistAssetType.Collection, count: 0 },
  });

  const [assetType, setAssetType] = useState<string>(ArtistAssetType.All);

  useEffect(() => {
    document.title = `Artist - ${PageDocument.Title}`;

    if (id == null || id === undefined) {
      return;
    }

    getArtistAsync(id);
    getArtistAssetsCountAsync(id);
  }, []);

  useEffect(() => {
    if (artist && artist.artist && artist.artist.nameInfoEn) {
      document.title = `${artist.artist.nameInfoEn.firstname} ${artist.artist.nameInfoEn.lastname} - ${PageDocument.Title}`;

      if (assetType === 'collection') {
        getArtistCollectionAsync(artist.artist.id);
        return;
      }

      getAssetByTypeAsync(artist.artist.id, assetType);
    }
  }, [artist]);

  useEffect(() => {
    if (assetType && artist && artist.artist) {
      if (assetType === 'collection') {
        getArtistCollectionAsync(artist.artist.id);
        return;
      }

      getAssetByTypeAsync(artist.artist.id, assetType);
    }
  }, [assetType]);

  const getArtistAsync = async (artistId: string) => {
    const { data, status } = await new AccountService().getArtistById(artistId);

    if (status !== HttpStatusCode.Ok) {
      setIsNotFound(true);

      return;
    }

    setArtist(data);
    onCheckFollowArtistAsync(data.artist.id);
  };

  const getArtistAssetsCountAsync = async (artistId: string) => {
    const {
      data,
      status
    } = await new ArtistService().getArtistAssetCountAsync(artistId);

    if (status !== HttpStatusCode.Ok) {
      const mapped: AssetCountResponseDto = {
        all: {
          assetType: data.all.assetType,
          count: 0,
        },
        photo: {
          assetType: data.photo.assetType,
          count: 0,
        },
        video: {
          assetType: data.video.assetType,
          count: 0,
        },
        collection: {
          assetType: data.collection.assetType,
          count: 0,
        },
      };

      setAssetCount(mapped);

      return;
    }

    const assetCountMapped: AssetCountResponseDto = {
      all: {
        assetType: data.all.assetType,
        count: data.all.count,
      },
      photo: {
        assetType: data.photo.assetType,
        count: data.photo.count,
      },
      video: {
        assetType: data.video.assetType,
        count: data.video.count,
      },
      collection: {
        assetType: data.collection.assetType,
        count: data.collection.count,
      },
    };

    setAssetCount(assetCountMapped);
  }

  const getAssetByTypeAsync = async (
    artistId: string,
    assetType?: string) => {

    const { data, status } =
      await new ArtistService()
        .getArtistAssetByTypeAsync(
          artistId,
          assetType == ArtistAssetType.All ? '' : assetType);

    if (status !== HttpStatusCode.Ok) {
      setAssets([]);
      return;
    }

    const modifiedData = data.map((asset: AssetPublishModel) => ({
      ...asset,
      artistFirstname: artist.artist.nameInfoEn.firstname,
      artistLastname: artist.artist.nameInfoEn.lastname,
      artistNameEn: artist.artist.nameInfoEn.firstname,
      imageOrientation: asset.orientation ?? asset.imageOrientation,
    }));

    setAssets(modifiedData);
    setAssetCount((prevState) => ({
      ...prevState,
      [assetType as keyof AssetCountResponseDto]: {
        ...prevState[assetType as keyof AssetCountResponseDto],
        count: data.length,
      },
    }));
  }

  const getArtistCollectionAsync = async (artistId: string) => {
    const { data, status } =
      await new ArtistService().getArtistAssetCollectionsAsync(artistId);

    if (status !== HttpStatusCode.Ok) {
      setCollections([]);
      return;
    }

    setCollections(data);
    setAssetCount((prevState) => ({
      ...prevState,
      [assetType as keyof AssetCountResponseDto]: {
        ...prevState['collection' as keyof AssetCountResponseDto],
        count: data.length,
      },
    }));
  }

  const onFollowArtistAsync = async (id: string) => {
    if (!(userProfile && userProfile.userId)) {
      messageApi.open({
        type: 'error',
        content: TranslationKey('loginToUseThisFeature'),
      });

      return;
    }

    const followCreate = {
      artistId: id,
    } as FollowCreateModel;
    const { status: responseStatus } = await new AccountService().followByArtistId(followCreate);

    if (responseStatus !== HttpStatus.CREATED) {
      messageApi.open({
        type: 'error',
        content: TranslationKey('follows.followFailed'),
      });
      return;
    }

    messageApi.open({
      type: 'success',
      content: TranslationKey('follows.followSuccess'),
    });
    onCheckFollowArtistAsync(id);
  };

  const onUnfollowArtistAsync = async (id: string) => {
    const { status: responseStatus } = await new AccountService().unfollowByArtistId(id);

    if (responseStatus !== HttpStatus.NO_CONTENT) {
      messageApi.open({
        type: 'error',
        content: TranslationKey('follows.unFollowFailed'),
      });
      return;
    }

    messageApi.open({
      type: 'success',
      content: TranslationKey('follows.unFollowSuccess'),
    });

    onCheckFollowArtistAsync(id);
  };

  const onCheckFollowArtistAsync = async (artistId: string) => {
    const { data: responseData, status: responseStatus } = await new AccountService().getFollower(artistId);

    if (responseStatus !== HttpStatus.OK) {
      setIsFollow(false);
      return;
    }

    setIsFollow(responseData.artistId === artistId);
  };

  const onClickShare = () => {
    setIsOpenShare(true);
  };

  const handleCloseShare = () => setIsOpenShare(false);

  const itemsTab: TabsProps['items'] = [
    {
      key: ArtistAssetType.All,
      label: TranslationKey('artistProfile.tabs.all') + ' (' + assetCount.all.count + ')',
      children: <GalleryArtist assets={assets} />,
    },
    {
      key: ArtistAssetType.Image,
      label: TranslationKey('artistProfile.tabs.photos') + ' (' + assetCount.photo.count + ')',
      children: <GalleryArtist assets={assets} />,
    },
    {
      key: ArtistAssetType.Video,
      label: TranslationKey('artistProfile.tabs.videos') + ' (' + assetCount.video.count + ')',
      children: <GalleryArtist assets={assets} />,
    },
    {
      key: ArtistAssetType.Collection,
      label: TranslationKey('artistProfile.tabs.collections') + ' (' + assetCount.collection.count + ')',
      children: <GalleryArtistCollection collections={collections} />,
    },
  ];

  if (isNotFound) {
    return (
      <Row className='pt-5' justify='center'>
        <Col span={24}>
          <Result
            status='404'
            title='404'
            subTitle='Sorry, the page you visited does not exist.'
            className='result-error'
            extra={[
              <a href='/' className='btn-orange-round' style={{ textDecoration: 'none' }}>
                {TranslationKey('goToHome')}
              </a>,
            ]}
          />
        </Col>
      </Row>
    );
  }

  return (
    <div className='search pt-5 profile banner-margin-t-50'>
      {contextHolder}
      <div className='container'>
        {
          artist &&
          (
            <>
              <Row justify='center' gutter={16}>
                <Col style={{ alignContent: 'center' }}>
                  {
                    artist.avatarUrl
                      ?
                      (
                        <Avatar
                          size={{ xs: 100, sm: 100, md: 100, lg: 100, xl: 160, xxl: 160 }}
                          src={<img src={artist.avatarUrl} alt={artist.artist.nameInfoEn.firstname + ' ' + artist.artist.nameInfoEn.lastname} />}
                        />
                      )
                      :
                      (
                        <Avatar
                          size={{ xs: 100, sm: 100, md: 100, lg: 100, xl: 160, xxl: 160 }}
                          icon={<UserOutlined />}
                          alt='artist'
                          style={{ backgroundColor: 'rgb(255, 85, 1)' }}
                        />
                      )}
                </Col>
              </Row>
              <Row justify='center' gutter={16} className='mt-4'>
                <Col span={24} style={{ textAlign: 'center' }}>
                  {artist.artist && artist.artist.nameInfoEn && (
                    <>
                      <Title className='text-light'>
                        {artist.artist.nameInfoEn.firstname +
                          ' ' +
                          artist.artist.nameInfoEn.lastname}
                      </Title>
                      <Text className='text-light'>{artist.artist.selfPromotion}</Text>
                    </>
                  )}
                </Col>
              </Row>
              <Row justify='center' gutter={16} className='mt-4'>
                <Col span={24} style={{ textAlign: 'center' }}>
                  <Space>
                    {
                      isFollow
                        ?
                        (
                          <Button
                            type='primary'
                            shape='round'
                            className='btn-outline-border active'
                            size='large'
                            onClick={() => onUnfollowArtistAsync(artist.artist.id)}
                          >
                            {TranslationKey('button.unFollow')}
                          </Button>
                        )
                        :
                        (
                          <Button
                            type='primary'
                            shape='round'
                            className='btn-light'
                            size='large'
                            onClick={() => onFollowArtistAsync(artist.artist.id)}
                          >
                            {TranslationKey('button.follow')}
                          </Button>
                        )}
                    <Button
                      type='primary'
                      shape='circle'
                      className='btn-outline'
                      size='large'
                      icon={<ShareAltOutlined className='icon-fix' />}
                      onClick={onClickShare}
                    />
                  </Space>
                </Col>
              </Row>
              <Row gutter={16} className='mt-4'>
                <Col span={24}>
                  <Tabs defaultActiveKey={ArtistAssetType.All}
                    items={itemsTab}
                    onChange={(key) => {
                      setAssetType(key);
                    }}
                    direction='ltr'
                    className='custom-tab mt-3' />
                </Col>
              </Row>
              <ShareModal
                isModalOpen={isOpenShare}
                sharedUrl={window.location.href}
                onClose={handleCloseShare}
              />
            </>
          )}
      </div>
    </div>
  );
}
